import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  FormControl,
  Grid,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
} from "@mui/material";
import PeopleIcon from "@mui/icons-material/People";
import GroupIcon from "@mui/icons-material/Group";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import SavingsIcon from "@mui/icons-material/Savings";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import PaidIcon from "@mui/icons-material/Paid";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import BarChartIcon from "@mui/icons-material/BarChart";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Doughnut } from "react-chartjs-2";
import LineChart from "../../components/LineChart";
import { useNavigate } from "react-router-dom";
import { useLazyGetDashboardQuery } from "../../services/dashboard";
import { Loader, showError } from "../../constants";
import useAuth from "../../hooks/useAuth";
import { Permissions } from "../../types/User";

ChartJS.register(ArcElement, Tooltip, Legend);

type TrendItem = {
  name: string;
  count: number;
};

type PlanBreakdownItem = {
  name: string;
  count: number;
};

type DashboardPayload = {
  totalCustomers?: number;
  activeCustomers?: number;
  paidCustomers?: number;
  freeCustomers?: number;
  trialUsers?: number;
  newCustomers?: number;
  newUsersDaily?: number;
  newUsersWeekly?: number;
  newUsersMonthly?: number;
  dailyActiveUsers?: number;
  monthlyActiveUsers?: number;
  bankAccountsLinked?: number;
  budgetsCreated?: number;
  subscriptionsDetected?: number;
  billsDetected?: number;
  totalRevenue?: number;
  monthlyRevenue?: number;
  noOfTransfers?: number;
  achqCost?: number;
  churnedCustomers?: number;
  churnRate?: number;
  planBreakdown?: PlanBreakdownItem[];
  result?: TrendItem[] | Record<string, number>;
};

type MetricCard = {
  label: string;
  value: string | number;
  subLabel?: string;
  icon: React.ReactNode;
};

const DashBoard = () => {
  const userData = useAuth();
  const navigate = useNavigate();
  const [userFilter, setUserFilter] = useState<string>("MONTH");
  const [dashboardData, setDashboardData] = useState<DashboardPayload>({});
  const [getDashBoardData, { isLoading }] = useLazyGetDashboardQuery();

  const userPermissions = useMemo(
    () =>
      userData?.permissions?.length ? JSON.parse(`${userData?.permissions}`) : [],
    [userData?.permissions]
  );

  const handleUserChange = (event: SelectChangeEvent) => {
    setUserFilter(event.target.value as string);
  };

  const getDashBoard = useCallback(async () => {
    try {
      const response = await getDashBoardData({
        type: userFilter,
        type2: "MONTH",
      }).unwrap();
      if (response?.statusCode === 200) {
        setDashboardData(response?.data || {});
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
    }
  }, [getDashBoardData, userFilter]);

  useEffect(() => {
    if (userData && userPermissions?.length) {
      const idx = userPermissions.findIndex(
        (ele: Permissions) => ele?.label === "Dashboard"
      );
      if (idx < 0) {
        navigate("/permission-denied", { replace: true });
      }
    }
  }, [userData, userPermissions, navigate]);

  useEffect(() => {
    getDashBoard();
  }, [getDashBoard]);

  const trendLabels = useMemo(() => {
    if (Array.isArray(dashboardData?.result)) {
      return dashboardData.result.map((item) => item?.name);
    }
    if (dashboardData?.result && typeof dashboardData.result === "object") {
      return Object.keys(dashboardData.result);
    }
    return [];
  }, [dashboardData?.result]);

  const trendValues = useMemo(() => {
    if (Array.isArray(dashboardData?.result)) {
      return dashboardData.result.map((item) => Number(item?.count || 0));
    }
    if (dashboardData?.result && typeof dashboardData.result === "object") {
      return Object.values(dashboardData.result).map((value) => Number(value || 0));
    }
    return [];
  }, [dashboardData?.result]);

  const customerTrendData = {
    labels: trendLabels,
    datasets: [
      {
        label: "Customer Growth",
        data: trendValues,
        borderColor: "#1FA6A0",
        backgroundColor: "rgba(31, 166, 160, 0.25)",
      },
    ],
  };

  const planBreakdown = dashboardData?.planBreakdown || [];
  const planMixData = {
    labels: planBreakdown.map((item) => item.name),
    datasets: [
      {
        label: "Customer Mix",
        data: planBreakdown.map((item) => Number(item.count || 0)),
        backgroundColor: ["#4C6EF5", "#F59F00", "#22B8CF", "#12B886", "#6B7280"],
        borderWidth: 1,
      },
    ],
  };

  const cards: MetricCard[] = [
    {
      label: "Total Customers",
      value: Number(dashboardData?.totalCustomers || 0),
      icon: <PeopleIcon />,
    },
    {
      label: "Active Customers",
      value: Number(dashboardData?.activeCustomers || 0),
      icon: <GroupIcon />,
    },
    {
      label: "Paid / Free / Trial",
      value: `${Number(dashboardData?.paidCustomers || 0)} / ${Number(
        dashboardData?.freeCustomers || 0
      )} / ${Number(dashboardData?.trialUsers || 0)}`,
      icon: <AccountTreeIcon />,
    },
    {
      label: "New Users (D/W/M)",
      value: `${Number(dashboardData?.newUsersDaily || dashboardData?.newCustomers || 0)} / ${Number(
        dashboardData?.newUsersWeekly || 0
      )} / ${Number(dashboardData?.newUsersMonthly || 0)}`,
      icon: <PersonAddAlt1Icon />,
    },
    {
      label: "DAU / MAU",
      value: `${Number(dashboardData?.dailyActiveUsers || 0)} / ${Number(
        dashboardData?.monthlyActiveUsers || 0
      )}`,
      icon: <BarChartIcon />,
    },
    {
      label: "Bank Accounts Linked",
      value: Number(dashboardData?.bankAccountsLinked || 0),
      icon: <SavingsIcon />,
    },
    {
      label: "Budgets / Subscriptions / Bills",
      value: `${Number(dashboardData?.budgetsCreated || 0)} / ${Number(
        dashboardData?.subscriptionsDetected || 0
      )} / ${Number(dashboardData?.billsDetected || 0)}`,
      icon: <ReceiptLongIcon />,
    },
    {
      label: "Revenue",
      value: `$${Number(dashboardData?.monthlyRevenue || dashboardData?.totalRevenue || 0).toFixed(2)}`,
      subLabel: `ACHQ Cost: $${Number(dashboardData?.achqCost || 0).toFixed(2)}`,
      icon: <PaidIcon />,
    },
    {
      label: "Churn",
      value: `${Number(dashboardData?.churnedCustomers || 0)} (${Number(
        dashboardData?.churnRate || 0
      ).toFixed(1)}%)`,
      icon: <TrendingDownIcon />,
    },
  ];

  return (
    <div className="main_loyout">
      <Loader isLoad={isLoading} />
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        className="dashboard"
        spacing={2}
      >
        <h1 className="mn_hdng">Dashboard</h1>
        <FormControl size="small" sx={{ minWidth: 170 }}>
          <Select value={userFilter} onChange={handleUserChange}>
            <MenuItem value="DAILY">Daily Trend</MenuItem>
            <MenuItem value="WEEK">Weekly Trend</MenuItem>
            <MenuItem value="MONTH">Monthly Trend</MenuItem>
            <MenuItem value="YEAR">Yearly Trend</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      <Grid container spacing={2} className="dashGrid">
        {cards.map((card) => (
          <Grid item lg={4} md={6} sm={6} xs={12} key={card.label}>
            <Card
              className="cards"
              sx={{
                borderRadius: "16px",
                border: "1px solid #e5e7eb",
                boxShadow: "0 8px 20px rgba(15, 23, 42, 0.06)",
              }}
            >
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                  <Box>
                    <Typography variant="body2" sx={{ color: "#64748b", mb: 0.5 }}>
                      {card.label}
                    </Typography>
                    <Typography variant="h5" sx={{ color: "#0f172a", fontWeight: 700 }}>
                      {card.value}
                    </Typography>
                    {card.subLabel ? (
                      <Typography variant="caption" sx={{ color: "#0f766e" }}>
                        {card.subLabel}
                      </Typography>
                    ) : null}
                  </Box>
                  <Box sx={{ color: "#0f766e", mt: 0.5 }}>{card.icon}</Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2} className="dashGraph" sx={{ pt: 3 }}>
        <Grid item lg={8} md={12} sm={12} xs={12}>
          <Card className="cards" sx={{ borderRadius: "16px", border: "1px solid #e5e7eb" }}>
            <CardContent>
              <Typography variant="h6" sx={{ color: "#0f172a", mb: 2 }}>
                Customer Growth Trend
              </Typography>
              <LineChart data={customerTrendData} />
            </CardContent>
          </Card>
        </Grid>
        <Grid item lg={4} md={12} sm={12} xs={12}>
          <Card className="cards" sx={{ borderRadius: "16px", border: "1px solid #e5e7eb" }}>
            <CardContent>
              <Typography variant="h6" sx={{ color: "#0f172a", mb: 2 }}>
                Plan Mix
              </Typography>
              <Box sx={{ maxWidth: 320, mx: "auto" }}>
                <Doughnut data={planMixData} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </div>
  );
};

export default DashBoard;
